import { useState } from "react";

import { ApiFehler } from "../api/client";
import { useBetriebLogo, useBetriebSpeichern } from "../api/hooks";
import type { Betrieb } from "../api/typen";
import { useAuth } from "../kontext/AuthKontext";
import { useToast } from "../kontext/ToastKontext";
import { useBlobUrl } from "../hooks/useBlobUrl";
import { Hinweis, Karte, Knopf, Spinner } from "./Basis";

function Rechtstexte({ betrieb, istAdmin }: { betrieb: Betrieb; istAdmin: boolean }) {
  const speichern = useBetriebSpeichern(betrieb.id);
  const toast = useToast();
  const [impressum, setImpressum] = useState(betrieb.impressum_url ?? "");
  const [datenschutz, setDatenschutz] = useState(betrieb.datenschutz_url ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const geaendert = impressum.trim() !== (betrieb.impressum_url ?? "") || datenschutz.trim() !== (betrieb.datenschutz_url ?? "");

  function sichern() {
    setFehler(null);
    speichern.mutate(
      { impressum_url: impressum.trim() || null, datenschutz_url: datenschutz.trim() || null },
      {
        onSuccess: (b) => {
          setImpressum(b.impressum_url ?? "");
          setDatenschutz(b.datenschutz_url ?? "");
          toast.erfolg("Gespeichert");
        },
        onError: () => setFehler("Bitte vollständige Webadressen eintragen, z. B. www.firma.de/impressum"),
      },
    );
  }

  const feld = "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm disabled:bg-neutral-50";
  return (
    <div className="space-y-3 border-t border-neutral-100 pt-4">
      <div>
        <h3 className="text-sm font-semibold">Impressum und Datenschutz des Betriebs</h3>
        <p className="mt-1 text-xs text-neutral-500">Stehen unten klein auf den Seiten für Endkunden – statt unserer.</p>
      </div>
      <label className="block text-xs font-semibold text-neutral-600">
        Impressum
        <input className={`${feld} mt-1 font-normal`} placeholder="www.firma.de/impressum" value={impressum} disabled={!istAdmin} onChange={(e) => setImpressum(e.target.value)} />
      </label>
      <label className="block text-xs font-semibold text-neutral-600">
        Datenschutzerklärung
        <input className={`${feld} mt-1 font-normal`} placeholder="www.firma.de/datenschutz" value={datenschutz} disabled={!istAdmin} onChange={(e) => setDatenschutz(e.target.value)} />
      </label>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {istAdmin && geaendert && (
        <Knopf variante="primaer" laedt={speichern.isPending} onClick={sichern}>Speichern</Knopf>
      )}
      {(!betrieb.impressum_url || !betrieb.datenschutz_url) && (
        <Hinweis art="warnung">
          Fehlt noch: Ohne diese Adressen stehen auf den Kundenseiten weder Impressum noch Datenschutz. Bitte beim Betrieb
          erfragen. Seine Datenschutzerklärung sollte die Anmeldung über einen Dienstleister abdecken.
        </Hinweis>
      )}
    </div>
  );
}

/**
 * Auftritt gegenüber den Endkunden dieses Betriebs: Logo und Name im Status-Link, im Kunden-Link und im PDF.
 * Endkunden sehen immer nur den Betrieb, nie AnmeldeMeister (Impressum/Datenschutz bleiben klein verlinkt).
 */
export function BetriebAuftritt({ betrieb }: { betrieb: Betrieb }) {
  const { benutzer } = useAuth();
  const istAdmin = benutzer?.rolle === "admin";
  const { hochladen, entfernen } = useBetriebLogo(betrieb.id);
  const toast = useToast();
  const [fehler, setFehler] = useState<string | null>(null);
  // updated_at im Pfad: nach einem neuen Logo wird das Bild neu geladen.
  const vorschau = useBlobUrl(betrieb.logo_vorhanden ? `/api/betriebe/${betrieb.id}/logo?v=${encodeURIComponent(betrieb.updated_at)}` : null);

  function gewaehlt(e: React.ChangeEvent<HTMLInputElement>) {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    setFehler(null);
    hochladen.mutate(datei, {
      onSuccess: () => toast.erfolg("Logo gespeichert"),
      onError: (err) => setFehler(err instanceof ApiFehler ? err.message : "Hochladen fehlgeschlagen"),
    });
  }

  return (
    <Karte className="space-y-4 p-5">
      <div>
        <h2 className="font-semibold">Auftritt gegenüber Endkunden</h2>
        <p className="mt-1 text-xs text-neutral-500">
          Endkunden sehen im Status-Link, im Kunden-Link (Formular und Vollmacht) und im PDF nur Logo und Namen von {betrieb.name},
          kein AnmeldeMeister. Die Vollmacht geht an den Betrieb.
        </p>
      </div>

      <div className="flex min-h-20 items-center justify-center rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4">
        {!betrieb.logo_vorhanden ? (
          <span className="text-sm text-neutral-400">Noch kein Logo</span>
        ) : vorschau.url ? (
          <img src={vorschau.url} alt={`Logo ${betrieb.name}`} className="max-h-16 w-auto" />
        ) : (
          <Spinner klein />
        )}
      </div>

      {istAdmin ? (
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-neutral-50">
            {hochladen.isPending && <Spinner klein />}
            {betrieb.logo_vorhanden ? "Anderes Logo hochladen" : "Logo hochladen"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={gewaehlt} disabled={hochladen.isPending} />
          </label>
          {betrieb.logo_vorhanden && (
            <Knopf variante="geist" laedt={entfernen.isPending} onClick={() => entfernen.mutate(undefined, { onError: (err) => toast.fehler(err, "Entfernen fehlgeschlagen") })}>
              Logo entfernen
            </Knopf>
          )}
        </div>
      ) : (
        <p className="text-xs text-neutral-500">Logo hochladen kann nur ein Admin.</p>
      )}
      <p className="text-xs text-neutral-500">PNG, JPG oder WebP, am besten mit transparentem Hintergrund. Wird auf höchstens 600 × 200 Pixel verkleinert.</p>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      <Rechtstexte key={betrieb.id} betrieb={betrieb} istAdmin={istAdmin} />
    </Karte>
  );
}
